import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import SEO from '../components/SEO';
import HeroCarousel from '../components/HeroCarousel';
import AWSMap from '../components/AWSMap';
import { getTestimonialsByPlacement } from '../data/testimonials';
import BundleCTA from '../components/BundleCTA';
import WebinarCTA from '../components/WebinarCTA';
import { websiteSchema } from '../data/seoSchemas';
import {
  Lock, LifeBuoy, Wrench, FlaskConical, UploadCloud, Database,
  Star, TrendingUp, Users, Server, Layers, ArrowRight,
  ChevronLeft, ChevronRight, CheckCircle, Globe,
} from 'lucide-react';
import { Button } from '../components/ui/button';

// ─────────────────────────────────────────────────────────────────────────────
// AnimatedSection — consistent scroll reveal
// ─────────────────────────────────────────────────────────────────────────────
const AnimatedSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Carousel slides
// ─────────────────────────────────────────────────────────────────────────────
const heroSlides = [
  {
    backgroundImage: 'https://images.unsplash.com/photo-1741795990628-7ec99d7d2044?w=1920&q=85',
    eyebrow: 'The SAP Business One Partner Platform',
    headline: 'One platform. One price. Per user, per month.',
    subheadline: "Cloud4Partners is no longer a collection of components. It's a complete platform for delivering and scaling SAP Business One: bundled, standardised, and built to help partners grow.",
    bullets: [
      'Fully bundled: no complicated bill of materials, no surprises',
      'Per user, per month: simple commercial model',
      'Built on Architecture 4.0: world-first SAP-certified AWS',
      'Migration Factory: structured migrations for your install base',
    ],
    primaryCta: 'Get in Touch',
    primaryCtaHref: '/contact',
    secondaryCta: 'Download the partner pack',
    secondaryCtaHref: '/migration-factory#partner-pack',
  },
  {
    backgroundImage: 'https://images.unsplash.com/photo-1757466762489-52fea38071ad?w=1920&q=85',
    eyebrow: 'Architecture 4.0',
    headline: 'Architecture 4.0. The new foundation for SAP Business One in the cloud.',
    subheadline: "AWS WorkSpaces, Okta, and the world's first SAP-certified next-generation AWS infrastructure designed specifically for SAP Business One. Modern, secure, continuously improving.",
    primaryCta: 'Explore Architecture 4.0',
    primaryCtaHref: '/architecture-4',
  },
  {
    backgroundImage: 'https://images.unsplash.com/photo-1644088379091-d574269d422f?w=1920&q=85',
    eyebrow: 'The Migration Factory',
    headline: 'The Migration Factory. Land. Expand. Grow.',
    subheadline: 'A repeatable, low-risk way to move your existing customers to the cloud, and unlock the recurring revenue sitting in your installed base.',
    primaryCta: 'See the Migration Factory',
    primaryCtaHref: '/migration-factory',
  },
  {
    backgroundImage: 'https://images.unsplash.com/photo-1586962016581-2a84a18fe5e1?w=1920&q=85',
    eyebrow: 'Everything included',
    headline: 'Everything included. No surprises.',
    subheadline: 'MFA, unlimited support, upgrade assistance, UAT, migration services, SQL to HANA, trial systems, and continuous performance: all bundled into one per-user-per-month platform.',
    primaryCta: "See what's included",
    primaryCtaHref: '/sap-b1-cloud-hosting',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Products
// ─────────────────────────────────────────────────────────────────────────────
const products = [
  {
    name: 'C4P Standard',
    badge: 'Multi-tenant',
    tagline: 'Your fastest path to SAP Business One in the cloud.',
    href: '/c4p-standard',
    icon: Users,
    gradient: 'from-cyan-600 to-blue-600',
    highlights: ['Per user, per month', 'Fast deployment', 'Fully bundled'],
  },
  {
    name: 'C4P Advanced',
    badge: 'Single-tenant',
    tagline: 'SAP Business One in the cloud, built for scale.',
    href: '/c4p-advanced',
    icon: Server,
    gradient: 'from-blue-600 to-purple-700',
    highlights: ['Dedicated infrastructure', 'Customer-controlled upgrades', 'Performance & isolation'],
  },
  {
    name: 'C4P MTPro',
    badge: 'Partner-built',
    tagline: 'Your own purpose-built multi-tenant environment.',
    href: '/mt-pro',
    icon: Layers,
    gradient: 'from-purple-600 to-pink-600',
    highlights: ['Partner-owned platform', 'Full control', 'Economy of scale yours'],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Feature bundle tiles
// ─────────────────────────────────────────────────────────────────────────────
const features = [
  { icon: Lock,       title: 'Multi-factor Authentication', description: 'Security built in, not bolted on.' },
  { icon: LifeBuoy,   title: 'Unlimited Support',           description: 'Expert help when your team or your customers need it.' },
  { icon: Wrench,     title: 'Upgrade Assistance',          description: 'We take the pain out of SAP upgrades.' },
  { icon: FlaskConical, title: 'Upgrade Test Environment', description: '30 days of upgrade test hosting, included. Most providers charge for this.' },
  { icon: UploadCloud, title: 'Migration Services',         description: 'Lift-and-shift, handled by a repeatable factory.' },
  { icon: Database,   title: 'SQL to HANA',                 description: 'Modernise the database without the project risk.' },
  { icon: Star,       title: 'Trial Systems',               description: 'Spin up sandboxes to pitch, prove, and win faster.' },
  { icon: TrendingUp, title: 'Continuous Performance',      description: 'Always-on tuning. Faster over time.' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Simple testimonial carousel
// ─────────────────────────────────────────────────────────────────────────────
const TestimonialCarousel = ({ items }) => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduced || paused || items.length <= 1) return;
    const id = setInterval(() => setCurrent(c => (c + 1) % items.length), 9000);
    return () => clearInterval(id);
  }, [paused, items.length, reduced]);

  const t = items[current];
  if (!t) return null;

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border-2 border-white/20 shadow-2xl max-w-2xl mx-auto" aria-live="polite">
        <div className="text-5xl text-cyan-400 mb-4">"</div>
        <p className="text-lg text-white leading-relaxed mb-6">{t.quote}</p>
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold flex-shrink-0">
            {t.author_name.charAt(0)}
          </div>
          <div>
            <div className="font-bold text-white text-sm">{t.author_name}</div>
            <div className="text-slate-400 text-xs">{t.author_role ? `${t.author_role}, ${t.author_company}` : t.author_company}</div>
          </div>
        </div>
      </div>
      {items.length > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button onClick={() => setCurrent(c => (c - 1 + items.length) % items.length)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-all" aria-label="Previous testimonial">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex gap-2">
            {items.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)} aria-label={`Testimonial ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? 'bg-white w-6' : 'bg-white/30 w-1.5'}`}
              />
            ))}
          </div>
          <button onClick={() => setCurrent(c => (c + 1) % items.length)} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-all" aria-label="Next testimonial">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export default function Home() {
  const navigate = useNavigate();
  const testimonials = getTestimonialsByPlacement('homepage').slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Cloud4Partners | The SAP Business One Partner Platform"
        description="The complete SAP Business One partner platform. C4P Standard, C4P Advanced, C4P MTPro. Fully bundled, per user per month, built on Architecture 4.0."
        keywords="SAP Business One cloud hosting, SAP B1 partner platform, C4P Standard, C4P Advanced, Architecture 4.0, Migration Factory"
        canonical="/"
        type="website"
        schemas={[websiteSchema()]}
      />

      {/* ─── Hero Carousel ─────────────────────────────────────────────────── */}
      <HeroCarousel slides={heroSlides} autoInterval={7000} />

      {/* ─── Partner credentials strip ─────────────────────────────────────── */}
      <section className="bg-slate-900 py-10 border-b border-white/5">
        <div className="container mx-auto px-4 lg:px-8">
          <p className="text-center text-slate-500 text-[10px] font-semibold uppercase tracking-widest mb-8">
            Official partner credentials
          </p>
          <div className="flex flex-wrap justify-center items-center gap-5">
            <div className="flex flex-col items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-8 py-6 min-w-[190px]">
              <img src="/logos/sap-b1-white.png" alt="SAP Business One" className="h-8 w-auto object-contain" style={{ mixBlendMode: 'screen', maxWidth: '150px' }} />
              <span className="text-slate-400 text-[9px] font-semibold uppercase tracking-widest text-center">SAP Premier Partner</span>
            </div>
            <div className="flex flex-col items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-8 py-6 min-w-[190px]">
              <div className="bg-white rounded-xl p-2.5 inline-flex items-center justify-center" style={{ minHeight: '48px', minWidth: '80px' }}>
                <img src="/logos/aws-advanced-partner.png" alt="AWS Advanced Partner" className="w-auto object-contain" style={{ height: '44px', maxWidth: '110px' }} />
              </div>
              <span className="text-slate-400 text-[9px] font-semibold uppercase tracking-widest text-center">AWS Advanced Partner</span>
            </div>
            <div className="flex flex-col items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-8 py-6 min-w-[190px] relative">
              <span className="absolute -top-2 right-4 bg-[#FF9900] text-white text-[8px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide leading-none">New</span>
              <img src="/logos/powered-by-aws-white.png" alt="Powered by AWS" className="h-8 w-auto object-contain" style={{ mixBlendMode: 'screen', maxWidth: '150px' }} />
              <span className="text-slate-400 text-[9px] font-semibold uppercase tracking-widest text-center">AWS SMB Competency</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Platform, in brief ─────────────────────────────────────────────── */}
      <section className="relative py-20 bg-gradient-to-br from-slate-50 via-blue-50/50 to-slate-50 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-[400px] h-[400px] bg-gradient-to-br from-blue-200/40 to-cyan-200/40 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 right-10 w-[400px] h-[400px] bg-gradient-to-br from-purple-200/30 to-blue-200/30 rounded-full blur-3xl animate-float-delayed" />
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(59 130 246) 1px, transparent 0)', backgroundSize: '50px 50px' }} />
          </div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14">
              <div className="inline-block mb-4">
                <span className="px-6 py-3 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200 rounded-full text-blue-900 text-sm font-bold uppercase tracking-wider">
                  The platform
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
                Everything your customers need. One price per user.
              </h2>
            </div>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: 'Fully bundled.',
                body: 'MFA, unlimited support, upgrade assistance, UAT environments, migration services, SQL to HANA, trial systems, and continuous performance: all included. No complicated bill of materials.',
                gradient: 'from-cyan-600 to-blue-600',
                icon: 'package',
              },
              {
                title: 'Per user, per month.',
                body: 'One simple commercial model. No infrastructure quoting. No component pricing. No surprises at renewal. You set your own margin.',
                gradient: 'from-blue-600 to-purple-600',
                icon: 'users',
              },
              {
                title: 'You keep the customer.',
                body: "You own the customer relationship and the services revenue. We own the platform. Cloud4Partners does not compete with its partners. Ever.",
                gradient: 'from-purple-600 to-pink-600',
                icon: 'shield',
              },
            ].map(({ title, body, gradient, icon }, i) => (
              <AnimatedSection key={title} delay={i * 0.1}>
                <div className="bg-white/80 backdrop-blur-md rounded-2xl p-8 border-2 border-blue-100 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                  {/* Icon inline with heading */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-md mt-0.5`}>
                      {icon === 'package' && (
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                        </svg>
                      )}
                      {icon === 'users' && (
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                      {icon === 'shield' && (
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 leading-snug min-h-[3.5rem]">{title}</h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{body}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Product cards ──────────────────────────────────────────────────── */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-cyan-600/10 to-blue-600/10 animate-gradient" />
          <div className="absolute top-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-float-delayed" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14">
              <span className="px-6 py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm border border-cyan-400/30 rounded-full text-cyan-300 text-sm font-bold uppercase tracking-wider">
                SAP Business One Cloud Hosting
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4 text-white leading-tight">
                Three ways to deliver SAP Business One in the cloud.
              </h2>
              <p className="text-slate-300 text-xl max-w-2xl mx-auto">Same platform. Same bundle. Different ways to consume it.</p>
            </div>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {products.map(({ name, badge, tagline, href, icon: Icon, gradient, highlights }, i) => (
              <AnimatedSection key={name} delay={i * 0.1}>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-white/20 hover:bg-white/10 transition-all duration-300 h-full flex flex-col" data-testid={`home-product-card-${name.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className={`bg-gradient-to-r ${gradient} p-5`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-xs font-bold text-white/70 uppercase tracking-wider">{badge}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white">{name}</h3>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <p className="text-slate-300 text-sm leading-relaxed mb-4 flex-1">{tagline}</p>
                    <ul className="space-y-1.5 mb-5">
                      {highlights.map(h => (
                        <li key={h} className="flex items-center gap-2 text-xs text-slate-400">
                          <CheckCircle className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                          {h}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => navigate(href)}
                      className="flex items-center justify-between w-full bg-white/8 hover:bg-white/15 text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition-all duration-200 border border-white/10 group"
                    >
                      Learn more
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection delay={0.3}>
            <div className="text-center mt-8">
              <button onClick={() => navigate('/sap-b1-cloud-hosting')} className="inline-flex items-center gap-2 text-cyan-300 hover:text-white font-semibold text-sm transition-colors group">
                Compare all plans
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── SAP B1 specific — the core differentiator ──────────────────────── */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/8 via-cyan-600/10 to-blue-600/8" />
          <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-cyan-500/6 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 left-20 w-[500px] h-[500px] bg-blue-500/8 rounded-full blur-3xl animate-float-delayed" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">

          {/* Header */}
          <AnimatedSection>
            <div className="text-center mb-16 max-w-4xl mx-auto">
              <span className="px-6 py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm border border-cyan-400/30 rounded-full text-cyan-300 text-sm font-bold uppercase tracking-wider">
                SAP Business One. Specifically.
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-5 text-white leading-tight">
                Built for SAP Business One from the ground up.
              </h2>
              <p className="text-slate-300 text-xl leading-relaxed">
                Any infrastructure provider can spin up a server. Very few understand the application it runs.
                We do, and that distinction is why 30+ SAP Business One partners trust C4P to underpin their customers.
              </p>
            </div>
          </AnimatedSection>

          {/* Four pillars */}
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-14">
            {[
              {
                gradient: 'from-cyan-600 to-blue-600',
                icon: (
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                ),
                headline: 'SAP Premier Partner: not just an AWS reseller',
                body: 'SAP Premier Partnership is the highest level of recognition SAP awards. SAP itself has validated our expertise with SAP Business One, not just our infrastructure credentials. Partners can tell their customers their hosting is SAP-endorsed.',
              },
              {
                gradient: 'from-blue-600 to-purple-600',
                icon: (
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                  </svg>
                ),
                headline: 'Every configuration tuned for SAP B1 workloads',
                body: 'SAP Business One on HANA has specific memory, compute, and network requirements. The platform is configured, monitored, and continuously optimised for exactly those workloads: not a generic template adapted for everything.',
              },
              {
                gradient: 'from-purple-600 to-pink-600',
                icon: (
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ),
                headline: 'A support team that knows the application',
                body: 'When something needs attention, our team can diagnose SAP-specific issues, not just confirm the server is running. We understand SAP Business One. Partners tell us this is one of the most important differences from every other provider they\'ve tried.',
              },
              {
                gradient: 'from-green-600 to-cyan-600',
                icon: (
                  <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                ),
                headline: 'Migrations built on SAP B1 knowledge',
                body: 'SQL to HANA conversion, upgrade paths, UAT environment provision: these require deep SAP Business One expertise to execute well. Generic infrastructure providers don\'t do this. We\'ve built and refined this capability over years, specifically for SAP B1.',
              },
            ].map(({ gradient, icon, headline, body }, i) => (
              <AnimatedSection key={headline} delay={i * 0.1}>
                <div className="bg-white/6 backdrop-blur-sm border border-white/10 rounded-2xl p-7 hover:bg-white/10 hover:border-white/20 transition-all duration-300 h-full flex flex-col">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      {icon}
                    </div>
                    <h3 className="text-lg font-bold text-white leading-snug min-h-[3.5rem]">{headline}</h3>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed flex-1">{body}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* World-first callout */}
          <AnimatedSection delay={0.4}>
            <div className="max-w-4xl mx-auto relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 rounded-2xl opacity-25 group-hover:opacity-40 blur-xl transition-opacity duration-500" />
              <div className="relative bg-gradient-to-r from-blue-900/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-8 border border-cyan-500/30 flex flex-col md:flex-row items-center gap-6">
                <div className="flex-1 text-center md:text-left">
                  <div className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-2">A world first for SAP Business One</div>
                  <p className="text-white text-lg font-semibold leading-relaxed">
                    Cloud4Partners is the first platform in the world to bring the new generation of SAP-certified AWS infrastructure designed specifically for SAP Business One to market.
                  </p>
                  <p className="text-slate-400 text-sm mt-2">
                    This is the default on every C4P Standard and C4P Advanced deployment.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/architecture-4')}
                  className="flex-shrink-0 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-sm px-6 py-3 rounded-xl transition-all duration-200 group/btn"
                >
                  See Architecture 4.0
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </AnimatedSection>

        </div>
      </section>

      {/* ─── Everything in the bundle ───────────────────────────────────────── */}
      <section className="relative py-24 bg-gradient-to-br from-slate-50 via-blue-50/50 to-slate-50 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-[500px] h-[500px] bg-gradient-to-br from-blue-200/40 to-cyan-200/40 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-gradient-to-br from-purple-200/40 to-blue-200/40 rounded-full blur-3xl animate-float-delayed" />
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(59 130 246) 1px, transparent 0)', backgroundSize: '50px 50px' }} />
          </div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14">
              <span className="px-6 py-3 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200 rounded-full text-blue-900 text-sm font-bold uppercase tracking-wider">
                What's included
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4 text-slate-900 leading-tight">
                Everything bundled. No complicated bill of materials. No surprises.
              </h2>
              <p className="text-slate-600 text-xl max-w-2xl mx-auto">
                Every C4P deployment includes all of the following. There's nothing extra to quote.
              </p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {features.map(({ icon: Icon, title, description }, i) => (
              <motion.div
                key={title}
                className="h-full"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border-2 border-blue-100 shadow-lg hover:shadow-xl hover:border-blue-200 transition-all duration-300 h-full text-center">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm mb-1.5">{title}</h3>
                  <p className="text-slate-500 text-xs leading-relaxed">{description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── AWS Global Reach ───────────────────────────────────────────────── */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-600/8 via-blue-600/10 to-cyan-600/8 animate-gradient" />
          <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-500/8 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-500/8 rounded-full blur-3xl animate-float-delayed" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="text-center mb-10">
              <span className="px-6 py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm border border-cyan-400/30 rounded-full text-cyan-300 text-sm font-bold uppercase tracking-wider flex items-center gap-2 justify-center w-fit mx-auto">
                <Globe className="w-4 h-4" />
                Global coverage
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mt-6 mb-4 text-white leading-tight">
                Your customers, wherever they are.
              </h2>
              <p className="text-slate-300 text-xl max-w-3xl mx-auto leading-relaxed">
                Nine SAP Business One-ready AWS regions across five continents. More global coverage than any other SAP B1 cloud platform, with full data sovereignty options in every region.
              </p>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <div className="max-w-6xl mx-auto">
              <AWSMap />
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.3}>
            <div className="text-center mt-8">
              <button
                onClick={() => navigate('/data-sovereignty')}
                className="inline-flex items-center gap-2 text-cyan-300 hover:text-white font-semibold text-sm transition-colors group"
              >
                Learn about data sovereignty options
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>


      {/* ─── Webinar CTA ─────────────────────────────────────────────────── */}
      <WebinarCTA
        headline="See the entire platform demonstrated live."
        body="Our free partner webinar series covers C4P Standard, C4P Advanced, Architecture 4.0, and the Migration Factory, from anywhere in the world, at a time that suits you."
        cta="Browse upcoming webinars"
      />

      {/* ─── Architecture 4.0 + Migration Factory teasers ──────────────────── */}
      <section className="relative py-24 bg-gradient-to-br from-slate-50 via-blue-50/50 to-slate-50 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 right-10 w-[400px] h-[400px] bg-gradient-to-br from-blue-200/30 to-purple-200/30 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 left-10 w-[400px] h-[400px] bg-gradient-to-br from-cyan-200/30 to-blue-200/30 rounded-full blur-3xl animate-float-delayed" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">

            {/* Architecture 4.0 */}
            <AnimatedSection delay={0.1}>
              <div className="relative group h-full">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500" />
                <div className="relative bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-8 text-white shadow-xl hover:shadow-2xl transition-all duration-300 h-full flex flex-col">
                  <div className="mb-4">
                    <span className="px-4 py-2 bg-white/20 rounded-full text-xs font-bold uppercase tracking-wider">Architecture 4.0</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-3">The platform foundation.</h3>
                  <p className="text-blue-50 leading-relaxed mb-6 flex-1">
                    AWS WorkSpaces, Okta identity, and the world's first SAP-certified next-generation AWS infrastructure designed specifically for SAP Business One. The default under every C4P deployment.
                  </p>
                  <button
                    onClick={() => navigate('/architecture-4')}
                    className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition-all duration-200 self-start group/btn"
                  >
                    Explore Architecture 4.0
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </AnimatedSection>

            {/* Migration Factory */}
            <AnimatedSection delay={0.2}>
              <div className="relative group h-full">
                <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500" />
                <div className="relative bg-white/80 backdrop-blur-md rounded-2xl p-8 border-2 border-blue-200 shadow-xl hover:shadow-2xl transition-all duration-300 h-full flex flex-col">
                  <div className="mb-4">
                    <span className="px-4 py-2 bg-gradient-to-r from-green-100 to-blue-100 border border-green-200 rounded-full text-green-900 text-xs font-bold uppercase tracking-wider">The Migration Factory</span>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Land. Expand. Grow.</h3>
                  <p className="text-slate-600 leading-relaxed mb-6 flex-1">
                    A structured, repeatable way to move your existing customers from on-premise to cloud and unlock the recurring revenue already in your install base.
                  </p>
                  <button
                    onClick={() => navigate('/migration-factory')}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-500 hover:to-blue-500 text-white font-semibold text-sm px-5 py-2.5 rounded-xl transition-all duration-200 self-start group/btn"
                  >
                    See the Migration Factory
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </AnimatedSection>

          </div>
        </div>
      </section>


      {/* ─── Bundle CTA ──────────────────────────────────────────────────── */}
      <BundleCTA
        headline="One price per user. Twelve features included."
        body="Many providers charge extra for MFA, SSO, UAT environments, upgrade assistance and more. See exactly what C4P bundles, and what others charge separately for."
        cta="See the full comparison"
      />

      {/* ─── Testimonials ───────────────────────────────────────────────────── */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-cyan-600/10 to-blue-600/10 animate-gradient" />
          <div className="absolute top-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-float-delayed" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <AnimatedSection>
            <div className="text-center mb-10">
              <span className="px-6 py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm border border-cyan-400/30 rounded-full text-cyan-300 text-sm font-bold uppercase tracking-wider">
                What partners say
              </span>
              <h2 className="text-4xl font-bold mt-6 mb-4 text-white">
                Hear from the partners building on our platform.
              </h2>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <TestimonialCarousel items={testimonials} />
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <div className="text-center mt-8">
              <button onClick={() => navigate('/testimonials')} className="inline-flex items-center gap-2 text-cyan-300 hover:text-white font-semibold text-sm transition-colors group" data-testid="home-all-testimonials-link">
                Read all partner stories
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ─── CTA Banner ─────────────────────────────────────────────────────── */}
      <section className="relative py-24 bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-700 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10 text-center">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Ready to see the platform? Let's talk.
            </h2>
            <p className="text-blue-100 text-xl mb-10 max-w-2xl mx-auto">
              Get in Touch, request the partner pack, or start a Migration Factory conversation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-blue-50 text-xl px-12 py-7 shadow-2xl font-bold"
                onClick={() => navigate('/contact')}
              >
                Get in Touch
                <ArrowRight className="ml-3 w-6 h-6" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/40 text-white hover:bg-white/15 text-xl px-12 py-7 font-bold"
                onClick={() => navigate('/migration-factory#partner-pack')}
              >
                Download the partner pack
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
